import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Crown, Check, Clock, ExternalLink, QrCode, Upload, History, AlertTriangle,
} from "lucide-react";
import { compressImage } from "@/lib/imageCompress";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; }
}
function daysLeft(iso) {
  try {
    const d = new Date(iso).getTime() - Date.now();
    return Math.ceil(d / 86400000);
  } catch { return 0; }
}

export default function BillingPage() {
  const [data, setData] = useState({ items: [] });
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradeFor, setUpgradeFor] = useState(null); // {school, plan}
  const [refNum, setRefNum] = useState("");
  const [receipt, setReceipt] = useState({ base64: "", mime: "" });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [me, pub, hist] = await Promise.all([
        api.get("/me/subscription"),
        api.get("/public/subscription-plans"),
        api.get("/me/subscription/payments"),
      ]);
      setData(me.data || { items: [] });
      setPlans(pub.data?.items || []);
      setPayments(hist.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startUpgrade = async (school, planSummary) => {
    try {
      // Fetch full plan with payment_link / qr / instructions
      const r = await api.get(`/me/subscription/${school.id}/plan-details`, { params: { plan_id: planSummary.id } });
      setUpgradeFor({ school, plan: r.data });
      setRefNum(""); setReceipt({ base64: "", mime: "" }); setNote("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleReceipt = async (file) => {
    if (!file) return;
    if (file.type === "application/pdf") {
      if (file.size > 1_200_000) return toast.error("PDF must be under 1.2MB");
      const reader = new FileReader();
      reader.onload = () => setReceipt({ base64: reader.result, mime: "application/pdf" });
      reader.readAsDataURL(file);
      return;
    }
    if (!file.type.startsWith("image/")) return toast.error("Only image or PDF receipts are allowed");
    try {
      const b64 = await compressImage(file, { maxKB: 500, maxDim: 1600 });
      setReceipt({ base64: b64, mime: "image/jpeg" });
    } catch { toast.error("Could not load receipt"); }
  };

  const submitPayment = async () => {
    if (!refNum.trim()) return toast.error("Reference number is required");
    if (!receipt.base64) return toast.error("Upload a receipt");
    setBusy(true);
    try {
      await api.post("/me/subscription/payments", {
        school_id: upgradeFor.school.id,
        plan_id: upgradeFor.plan.id,
        amount: upgradeFor.plan.price_php,
        reference_number: refNum.trim(),
        receipt_base64: receipt.base64,
        receipt_mime: receipt.mime,
        note: note.trim() || null,
      });
      toast.success("Receipt submitted — waiting for Super Admin approval.");
      setUpgradeFor(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <><p className="text-center py-12 text-slate-500">Loading…</p></>;

  return (
    <>
      <div className="space-y-6" data-testid="billing-page">
        <div>
          <p className="text-xs text-slate-400 uppercase font-heading">Billing</p>
          <h1 className="font-heading text-3xl font-bold">Subscription</h1>
          <p className="text-sm text-brand-inkMuted mt-1">View your current plan, upgrade, and track payment history.</p>
        </div>

        {data.items.length === 0 && (
          <Card className="p-8 rounded-3xl text-center text-slate-500">No approved schools yet.</Card>
        )}

        {data.items.map((s) => {
          const expSoon = !s.plan?.is_free && daysLeft(s.subscription.expires_at) <= 7 && daysLeft(s.subscription.expires_at) > 0;
          const expired = s.subscription.status === "expired";
          return (
            <Card key={s.school.id} className="p-6 rounded-3xl border-[#2563EB]/10 space-y-5" data-testid={`billing-school-${s.school.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-heading text-xl font-bold flex items-center gap-2">{s.school.name}</h2>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-sm text-slate-500">Current plan:</span>
                    <span className="font-semibold flex items-center gap-1">
                      {s.plan?.is_free && <Crown className="w-4 h-4 text-amber-500" />}
                      {s.plan?.name || "Free"}
                    </span>
                    {!s.plan?.is_free && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${expired ? "bg-rose-50 text-rose-700" : expSoon ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                        {expired ? "Expired" : expSoon ? `Expires in ${daysLeft(s.subscription.expires_at)}d` : `Active until ${fmtDate(s.subscription.expires_at)}`}
                      </span>
                    )}
                  </div>
                </div>
                {s.latest_payment?.status === "pending" && (
                  <div className="bg-amber-50 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Payment under review</div>
                )}
              </div>

              {expired && (
                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3 flex items-start gap-2 text-sm text-rose-800">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  <div>Subscription expired. Your records are preserved but advanced modules are limited. Renew to restore full access.</div>
                </div>
              )}

              <div>
                <div className="text-xs uppercase font-bold text-slate-500 mb-2">Available plans</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {plans.map((p) => {
                    const isCurrent = s.plan?.id === p.id;
                    return (
                      <div key={p.id} className={`rounded-2xl p-4 border ${isCurrent ? "border-[#2563EB] bg-[#EFF6FF]" : "border-slate-200 bg-white"}`}>
                        {p.badge && <span className="text-[10px] uppercase font-bold tracking-wider text-[#1D4ED8] bg-[#DBEAFE] rounded-full px-2 py-0.5 inline-block mb-1">{p.badge}</span>}
                        <div className="font-heading font-bold text-lg">{p.name}</div>
                        <div className="text-2xl font-bold text-[#1D4ED8]">
                          {p.is_free ? "Free" : <>₱{p.price_php.toLocaleString()}<span className="text-xs text-slate-500 font-normal"> / {p.billing_cycle}</span></>}
                        </div>
                        {p.description && <p className="text-xs text-slate-600 mt-1 line-clamp-3">{p.description}</p>}
                        <div className="text-xs text-slate-500 mt-2">{p.modules?.length || 0} modules</div>
                        <div className="mt-3">
                          {isCurrent ? (
                            <span className="text-xs font-semibold text-[#1D4ED8] inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Current plan</span>
                          ) : p.is_free ? (
                            <span className="text-xs text-slate-400">Default tier</span>
                          ) : (
                            <Button size="sm" onClick={() => startUpgrade(s.school, p)} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid={`upgrade-${s.school.id}-${p.id}`}>
                              Upgrade
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          );
        })}

        <Card className="p-6 rounded-3xl border-[#2563EB]/10" data-testid="billing-history">
          <h2 className="font-heading text-xl font-bold mb-3 flex items-center gap-2"><History className="w-4 h-4" /> Payment history</h2>
          {payments.length === 0 ? (
            <p className="text-sm text-slate-500">No payments yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr><th className="text-left">When</th><th className="text-left">School</th><th className="text-left">Plan</th><th className="text-left">Amount</th><th className="text-left">Ref #</th><th className="text-left">Status</th></tr></thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id}>
                      <td className="text-xs">{fmtDate(p.submitted_at)}</td>
                      <td>{p.school_name}</td>
                      <td>{p.plan_name}</td>
                      <td>₱{(p.amount || 0).toLocaleString()}</td>
                      <td><span className="font-mono text-xs">{p.reference_number}</span></td>
                      <td>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${p.status === "approved" ? "bg-emerald-50 text-emerald-700" : p.status === "rejected" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{p.status}</span>
                        {p.rejection_reason && <div className="text-[11px] text-rose-600 mt-0.5">{p.rejection_reason}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Dialog open={!!upgradeFor} onOpenChange={(v) => { if (!v) setUpgradeFor(null); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle>Upgrade {upgradeFor?.school.name} → {upgradeFor?.plan?.name}</DialogTitle>
            <DialogDescription>Pay via the link or QR, then upload your receipt and reference number.</DialogDescription>
          </DialogHeader>
          {upgradeFor && (
            <div className="space-y-4">
              <div className="bg-[#EFF6FF] rounded-2xl p-3 grid sm:grid-cols-2 gap-3 items-start">
                <div>
                  <div className="text-xs text-slate-500">Amount</div>
                  <div className="text-2xl font-bold text-[#1D4ED8]">₱{upgradeFor.plan.price_php.toLocaleString()}</div>
                  <div className="text-xs text-slate-500">{upgradeFor.plan.billing_cycle} · {upgradeFor.plan.duration_days} days</div>
                  {upgradeFor.plan.payment_link && (
                    <a href={upgradeFor.plan.payment_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#1D4ED8] hover:underline mt-2"><ExternalLink className="w-4 h-4" /> Open payment link</a>
                  )}
                </div>
                {upgradeFor.plan.qr_image_base64 && (
                  <div className="flex flex-col items-center">
                    <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><QrCode className="w-3.5 h-3.5" /> Scan to pay</div>
                    <img src={upgradeFor.plan.qr_image_base64} alt="QR" className="w-40 h-40 rounded-2xl object-cover border border-slate-200" />
                  </div>
                )}
              </div>
              {upgradeFor.plan.payment_instructions && (
                <div className="text-sm bg-slate-50 rounded-2xl p-3 whitespace-pre-wrap">{upgradeFor.plan.payment_instructions}</div>
              )}
              <div className="border border-slate-200 rounded-2xl p-3 space-y-3">
                <div>
                  <Label>Reference number</Label>
                  <Input value={refNum} onChange={(e) => setRefNum(e.target.value)} placeholder="Transaction / receipt #" data-testid="payment-reference" />
                </div>
                <div>
                  <Label>Upload receipt (image or PDF)</Label>
                  {!receipt.base64 ? (
                    <label className="mt-1 inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 cursor-pointer">
                      <Upload className="w-4 h-4" /> Choose file
                      <input type="file" accept="image/*,application/pdf" hidden onChange={(e) => handleReceipt(e.target.files?.[0])} data-testid="payment-receipt-input" />
                    </label>
                  ) : (
                    <div className="mt-1 flex items-start gap-2">
                      {receipt.mime.startsWith("image/") ? (
                        <img src={receipt.base64} alt="receipt preview" className="max-h-40 rounded-2xl border border-slate-200" />
                      ) : (
                        <span className="text-sm text-slate-700">PDF receipt attached.</span>
                      )}
                      <button onClick={() => setReceipt({ base64: "", mime: "" })} className="text-xs text-rose-500 hover:underline">Remove</button>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Note (optional)</Label>
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeFor(null)}>Cancel</Button>
            <Button onClick={submitPayment} disabled={busy} className="bg-[#2563EB]" data-testid="payment-submit">Submit payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
